/**
 * AcenteMonthlyReport — Aylık performans raporu (özel acente + acente).
 *
 * Bir bakışta: bu ayın 3 KPI kartı, son 12 ayın çubuk grafiği ve tam
 * dönem tablosu. Backend `GET /api/panel/my-monthly-summary` çağırır —
 * özel acenteye yalnızca kendi acentesinin verisi döner (backend filter).
 */
import React, { useEffect, useMemo, useState } from "react";
import panelApi from "@/lib/panelApi";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell,
} from "recharts";
import {
  Loader2, TrendingUp, LogIn, LogOut, Wallet, Calendar, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import * as XLSX from "xlsx";

const TR_MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const TR_MONTHS_FULL = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

function labelShort(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return `${TR_MONTHS[(m || 1) - 1]} ${String(y).slice(-2)}`;
}
function labelFull(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return `${TR_MONTHS_FULL[(m || 1) - 1]} ${y}`;
}
function fmtSar(n) {
  return `${Number(n || 0).toLocaleString("tr-TR")} ﷼`;
}

function KpiCard({ icon: Icon, label, value, sub, accent = "brand-navy" }) {
  return (
    <div
      className={`rounded-2xl bg-white border border-${accent}/10 shadow-sm p-5`}
      data-testid={`monthly-kpi-${label.replace(/\s/g, "-").toLowerCase()}`}
    >
      <div className={`w-11 h-11 rounded-xl bg-${accent}/10 text-${accent} flex items-center justify-center`}>
        <Icon size={20} />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-3 font-semibold">{label}</div>
      <div className="font-serif text-3xl text-brand-navy mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function AcenteMonthlyReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    panelApi.get("/my-monthly-summary?months=12")
      .then((r) => setData(r.data || null))
      .catch((e) => setErr(e?.response?.data?.detail || "Veri alınamadı"))
      .finally(() => setLoading(false));
  }, []);

  const months = data?.months || [];
  const thisMonth = data?.this_month || { checkins: 0, checkouts: 0, revenue: 0 };

  // Aggregate 12-month totals for the "yıllık özet" strip
  const yearTotals = useMemo(() => {
    return months.reduce((acc, m) => {
      acc.checkins += m.checkins || 0;
      acc.checkouts += m.checkouts || 0;
      acc.revenue += m.revenue || 0;
      return acc;
    }, { checkins: 0, checkouts: 0, revenue: 0 });
  }, [months]);

  const chartData = useMemo(
    () => months.map((m) => ({
      name: labelShort(m.month),
      full: labelFull(m.month),
      giris: m.checkins || 0,
      cikis: m.checkouts || 0,
      ciro: Math.round(m.revenue || 0),
    })),
    [months]
  );

  const exportXlsx = () => {
    const header = ["Ay", "Giriş", "Çıkış", "Ciro (﷼)"];
    const rows = months.map((m) => [
      labelFull(m.month), m.checkins || 0, m.checkouts || 0, Math.round(m.revenue || 0),
    ]);
    // Totals row
    rows.push(["TOPLAM (12 ay)", yearTotals.checkins, yearTotals.checkouts, Math.round(yearTotals.revenue)]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    ws["!cols"] = [{ wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Aylık Rapor");
    XLSX.writeFile(wb, `aylik-rapor-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-slate-500" data-testid="monthly-loading">
        <Loader2 size={22} className="mx-auto mb-2 animate-spin text-brand-orange" />
        Aylık rapor hazırlanıyor…
      </div>
    );
  }
  if (err) {
    return (
      <div className="p-10 text-center text-rose-600" data-testid="monthly-error">
        {err}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="acente-monthly-report">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2 inline-flex items-center gap-1">
            <TrendingUp size={12} /> Aylık Rapor
          </div>
          <h1 className="font-serif text-3xl text-brand-navy">Bu Ay & Son 12 Ay</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Kendi giriş & çıkışlarınızı ve toplanan parayı ay bazında takip edin.
            İptal edilen rezervasyonlar hesaba katılmaz.
          </p>
        </div>
        <Button variant="outline" onClick={exportXlsx} data-testid="monthly-export">
          <Calendar size={14} className="mr-1" /> Excel'e İndir
        </Button>
      </header>

      {/* Bu Ay KPI's — 3 büyük kart */}
      <div className="grid gap-3 sm:grid-cols-3" data-testid="monthly-kpis">
        <KpiCard
          icon={LogIn}
          label="Bu Ay Giriş"
          value={thisMonth.checkins}
          sub="Bu ay giriş yapan grup sayısı"
          accent="emerald-600"
        />
        <KpiCard
          icon={LogOut}
          label="Bu Ay Çıkış"
          value={thisMonth.checkouts}
          sub="Bu ay çıkış yapan grup sayısı"
          accent="brand-navy"
        />
        <KpiCard
          icon={Wallet}
          label="Bu Ay Ciro"
          value={fmtSar(thisMonth.revenue)}
          sub="Toplanan para (SAR)"
          accent="brand-orange"
        />
      </div>

      {/* 12 aylık çubuk grafik */}
      <div className="rounded-2xl bg-white border border-brand-navy/10 shadow-sm p-4 sm:p-5" data-testid="monthly-chart-wrap">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Son 12 Ay</div>
            <h2 className="font-serif text-xl text-brand-navy">Aylık Giriş, Çıkış & Ciro</h2>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-slate-600">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> Giriş</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-brand-navy inline-block" /> Çıkış</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-brand-orange inline-block" /> Ciro</span>
          </div>
        </div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#c2410c" }} />
              <Tooltip
                contentStyle={{ borderRadius: 12, borderColor: "#e2e8f0", fontSize: 12 }}
                labelFormatter={(_, p) => p?.[0]?.payload?.full || ""}
                formatter={(v, key) => key === "ciro" ? [fmtSar(v), "Ciro"] : [v, key === "giris" ? "Giriş" : "Çıkış"]}
              />
              <Bar yAxisId="left" dataKey="giris" fill="#059669" radius={[6, 6, 0, 0]} maxBarSize={22} />
              <Bar yAxisId="left" dataKey="cikis" fill="#0f3057" radius={[6, 6, 0, 0]} maxBarSize={22} />
              <Bar yAxisId="right" dataKey="ciro" fill="#f97316" radius={[6, 6, 0, 0]} maxBarSize={22}>
                {chartData.map((_, i) => (<Cell key={i} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 12 aylık tablo */}
      <div className="rounded-2xl bg-white border border-brand-navy/10 shadow-sm overflow-hidden" data-testid="monthly-table">
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-end justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Detaylı Tablo</div>
            <h2 className="font-serif text-xl text-brand-navy">Ay Ay Kayıt Dökümü</h2>
          </div>
          <div className="text-xs text-slate-600 flex items-center gap-3 flex-wrap">
            <span data-testid="monthly-year-checkins">Yıllık Giriş: <b className="text-brand-navy">{yearTotals.checkins}</b></span>
            <span data-testid="monthly-year-checkouts">Yıllık Çıkış: <b className="text-brand-navy">{yearTotals.checkouts}</b></span>
            <span data-testid="monthly-year-revenue">Yıllık Ciro: <b className="text-brand-navy">{fmtSar(yearTotals.revenue)}</b></span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-canvas-stone text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left py-2 px-4 font-semibold">Ay</th>
                <th className="text-right py-2 px-4 font-semibold">Giriş</th>
                <th className="text-right py-2 px-4 font-semibold">Çıkış</th>
                <th className="text-right py-2 px-4 font-semibold">Ciro</th>
              </tr>
            </thead>
            <tbody>
              {months.length === 0 ? (
                <tr><td colSpan={4} className="py-8 text-center text-slate-500">Bu dönemde kayıt yok.</td></tr>
              ) : months.slice().reverse().map((m) => (
                <tr key={m.month} className="border-t border-slate-100 hover:bg-canvas-cream/40" data-testid={`monthly-row-${m.month}`}>
                  <td className="py-2 px-4 font-medium text-brand-navy">{labelFull(m.month)}</td>
                  <td className="py-2 px-4 text-right font-mono">{m.checkins || 0}</td>
                  <td className="py-2 px-4 text-right font-mono">{m.checkouts || 0}</td>
                  <td className="py-2 px-4 text-right font-mono text-brand-orange-dark">{fmtSar(m.revenue)}</td>
                </tr>
              ))}
            </tbody>
            {months.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-canvas-cream/60 font-semibold">
                  <td className="py-2 px-4">Toplam (12 ay)</td>
                  <td className="py-2 px-4 text-right font-mono">{yearTotals.checkins}</td>
                  <td className="py-2 px-4 text-right font-mono">{yearTotals.checkouts}</td>
                  <td className="py-2 px-4 text-right font-mono text-brand-orange-dark">{fmtSar(yearTotals.revenue)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
